/* eslint-disable no-nested-ternary */

import React, { useEffect, useState } from 'react'
import {
	Form,
	Input,
	Button,
	Select,
	Radio,
	Checkbox,
	message,
	Divider,
	Upload,
	Typography,
	Space,
	InputNumber,
	Row,
	Col,
	Dropdown,
	Menu,
} from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
// eslint-disable-next-line import/no-extraneous-dependencies
import { DefaultOptionType } from 'rc-select/lib/Select.d'
import countryCodeData from 'data/countryCodeData'
import addClient, { AddClientBody } from 'api/clients/addClient'
import { getClientsAction } from 'slices/client'
import handleFormError from 'utils/handleFormError'
import {
	LoadingOutlined,
	PlusOutlined,
	MinusCircleOutlined,
} from '@ant-design/icons'
import upload, { FileUpload } from 'api/file/upload'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import handleAxiosError from 'utils/handleAxiosError'
import ImgCrop from 'antd-img-crop'
import {
	Currency,
	supportedCurrencies,
	ClientTaxation,
	allBillingCycle,
	BillingCycle,
	ClientInvoiceDetails,
	AllowedModule,
	EdgePlatform,
	edgePlatforms,
	PlanDetails,
} from 'types'

import toTileCase from 'utils/toTitleCase'
import getAdmins from 'api/admin/getAdmins'
import isValidEmail from 'utils/isValidEmail'
import getPlatformLabel from 'utils/getPlatformLabel'
import { normalizeClientChargeForSave } from 'utils/normalizeClientCharge'
import AllowedModulesTable from 'component/Client/AllowedModulesTable'
import getClientPlanData from 'api/clients/getClientPlanData'
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuClickEventHandler } from 'rc-menu/lib/interface'

const { Item, List } = Form
const { Title } = Typography

interface PropTypes {
	closeDrawer: Function
}
interface FormBodyTypes {
	logo: string | null
	address: string
	billingCycle: 'MONTHLY' | 'YEARLY'
	city: string
	country: string
	country_code: string
	email: string
	title: string
	mobile: string
	organizationName: string
	billingCycleAmount: string
	state: string
	storageAllowed: string
	website: string
	whatcxCharge: string
	whatcxChargeInbound: number
	whatcxChargeOutbound: number
	currency_selected: Currency
	taxation: ClientTaxation[]
	invoiceDetails: ClientInvoiceDetails[]
	csmUser: string[]
	notificationEmails: string[]
	invoiceEmails: string[]
	shouldGenerateAutoInvoice: boolean
	allowedModules: AllowedModule[]
	platform: EdgePlatform
	tdsEnabled: boolean
	broadcastLimit: number
}

const prefixSelector = (
	<Item name='country_code' noStyle>
		<Select
			dropdownClassName='noMinWidth'
			showSearch
			optionFilterProp='children'
			placeholder='+91'
		>
			{countryCodeData.map(option => (
				<Select.Option key={option.id} value={option.code}>
					{`+${option.phone}`}
				</Select.Option>
			))}
		</Select>
	</Item>
)

/**
 * Form Component for adding Clients in client table
 * AddClientForm sends organizationName, email, address, city, state, website, country, mobileNumber and plan details
   to backend and hits addClient api to creates a new client with the same details
 * @param closeDrawer - function which get triggered when Add Client drawer get close
 * @returns just create a new client with the given details
 */

const AddClientForm: React.FC<PropTypes> = ({ closeDrawer }) => {
	const dispatch = useAppDispatch()

	const [form] = Form.useForm<FormBodyTypes>()

	const [loading, setLoading] = useState<boolean>(false)
	const [uploadError, setUploadError] = useState(false)
	const [csmUsersOptions, setCsmUsersOptions] = useState<DefaultOptionType[]>([])
	const [plans, setPlans] = useState<PlanDetails[]>([])
	const { currentPage } = useAppSelector(state => state.client)
	const [clientBillingCycle, setClientBillingCycle] =
		useState<BillingCycle>('MONTHLY')

	const allowedModules: AllowedModule[] | undefined = Form.useWatch(
		'allowedModules',
		form
	)

	/**
	 * Upload file function
	 */
	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			setLoading(true)
			const { url } = await upload(options)
			form.setFieldsValue({
				logo: url,
			})
			setLoading(false)
		} catch (error: any) {
			setLoading(false)
			handleAxiosError(error)
		}
	}

	const beforeUpload = (file: File) => {
		const isImage = file.type === 'image/png' || file.type === 'image/jpeg'
		if (!isImage) {
			message.error(`Only Image file accepted`)
			setUploadError(true)
		} else if (file.size > 1024 * 1024 * 1) {
			message.error('Image must be less than 1MB')
			setUploadError(true)
			return false
		}
		setUploadError(false)
		return isImage || Upload.LIST_IGNORE
	}

	const onFinish = async (values: FormBodyTypes) => {
		const body: AddClientBody = {
			plan: {
				title: values.title,
				storageAllowed: parseInt(values.storageAllowed, 10) * (1024 * 1024 * 1024),
				billingCycle: values.billingCycle,
				perMonth:
					values.billingCycle === 'MONTHLY'
						? parseInt(values.billingCycleAmount, 10)
						: 0,
				perYear:
					values.billingCycle === 'YEARLY'
						? parseInt(values.billingCycleAmount, 10)
						: 0,
				// modules: values.allowedModules,
				modules: values.allowedModules.map(m =>
					m.type === 'EDGE_CX_AI' || m.type === 'EXTERNAL_BOT'
						? { ...m, count: 0 }
						: m
				),
			},
			logo: values.logo || undefined,
			organizationName: values.organizationName,
			email: values.email,
			address: values.address,
			city: values.city,
			state: values.state,
			website: values.website || undefined,
			country: values.country,
			mobileNumber: {
				mobile: values.mobile,
				country: values.country_code === undefined ? 'IN' : values.country_code,
			},
			whatcxCharge: normalizeClientChargeForSave(values.whatcxCharge),
			whatcxChargeInbound: normalizeClientChargeForSave(
				values.whatcxChargeInbound
			),
			whatcxChargeOutbound: normalizeClientChargeForSave(
				values.whatcxChargeOutbound
			),
			currency_selected: values.currency_selected,
			taxation: values.taxation,
			csmUser: values.csmUser,
			notificationEmails: values.notificationEmails.join(','),
			invoiceEmails: values.invoiceEmails.join(','),
			invoiceDetails: values.invoiceDetails,
			shouldGenerateAutoInvoice: values.shouldGenerateAutoInvoice,
			platform: values.platform,
			tdsEnabled: values.tdsEnabled,
			broadcastLimit: Number(values.broadcastLimit),
		}
		try {
			setLoading(true)
			await addClient(body)
			setLoading(false)
			const options = {
				page: currentPage,
				size: 10,
				query: '',
			}
			dispatch(getClientsAction(options))
			closeDrawer()
			message.success('New Client Created')
		} catch (error: any) {
			setLoading(false)
		}
	}

	const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
		handleFormError(errorInfo)
	}
	const handlePlanSelect: MenuClickEventHandler = event => {
		const selectedPlan = plans.find(ele => ele.id === event.key)

		if (!selectedPlan) {
			message.error('Plan does not exist ')
			return
		}

		form.setFieldsValue({
			title: selectedPlan.title,
			allowedModules: selectedPlan.allowedModules,
			storageAllowed: String(selectedPlan.storageAllowed / (1024 * 1024 * 1024)),
			billingCycle: selectedPlan.billingCycle,
			billingCycleAmount:
				selectedPlan.billingCycle === 'MONTHLY'
					? String(selectedPlan.perMonth)
					: selectedPlan.billingCycle === 'YEARLY'
					? String(selectedPlan.perYear)
					: '0',
		})
		setClientBillingCycle(selectedPlan.billingCycle)
	}

	useEffect(() => {
		const loadCsmUserOptions = async () => {
			try {
				const admins = await getAdmins({ page: 1, size: 200 })
				const options = admins.records.map(admin => ({
					label: `${admin.firstName} ${admin.lastName} (${admin.email} - ${admin.mobile})`,
					value: admin._id,
				}))
				setCsmUsersOptions([...options])
			} catch (error: any) {
				handleAxiosError(error)
			}
		}
		loadCsmUserOptions()

		const fetchClientData = async () => {
			try {
				const moduleData = await getClientPlanData()
				setPlans(moduleData.plans)
			} catch (error) {
				setPlans([])
			}
		}
		fetchClientData()
	}, [])

	return (
		<Form
			form={form}
			name='addClient'
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			layout='vertical'
			initialValues={{
				logo: null,
				title: '',
				billingCycle: 'MONTHLY',
				address: '',
				city: '',
				country: '',
				country_code: 'IN',
				email: '',
				mobile: '',
				organizationName: '',
				billingCycleAmount: '',
				state: '',
				storageAllowed: '',
				website: '',
				whatcxCharge: '',
				whatcxChargeInbound: -1,
				whatcxChargeOutbound: -1,
				currency_selected: null,
				taxation: [],
				invoiceDetails: [],
				csmUser: [],
				notificationEmails: [],
				invoiceEmails: [],
				shouldGenerateAutoInvoice: true,
				allowedModules: [],
				platform: null,
				tdsEnabled: false,
				broadcastLimit: 0,
			}}
		>
			<Item
				label='Organization Name'
				name='organizationName'
				rules={[
					{
						required: true,
						message: 'Organization name is required',
					},
				]}
			>
				<Input />
			</Item>

			<Item
				label='Upload Logo'
				name='logo'
				extra='Recommended resolution is 640*640 with file size less than 1MB,
            keep visual elements centered'
			>
				<ImgCrop modalTitle='Crop Image'>
					<Upload
						name='logo'
						maxCount={1}
						beforeUpload={beforeUpload}
						showUploadList={!uploadError}
						multiple={false}
						customRequest={uploadFile}
						listType='picture-card'
						accept='image/*'
						onRemove={() =>
							form.setFieldsValue({
								logo: null,
							})
						}
					>
						<div>
							{loading ? <LoadingOutlined /> : <PlusOutlined />}
							<div style={{ marginTop: 8 }}>Upload</div>
						</div>
					</Upload>
				</ImgCrop>
			</Item>

			<Item
				label='Email'
				name='email'
				rules={[
					{
						required: true,
						message: 'Email is required',
					},

					{
						pattern:
							/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
						message: 'Please enter a valid Email',
					},
				]}
			>
				<Input />
			</Item>

			<Item
				name='mobile'
				label='Mobile Number'
				rules={[
					{
						required: true,
						message: 'Mobile is required',
					},
				]}
			>
				<Input
					addonBefore={prefixSelector}
					style={{ width: '100%' }}
					placeholder='xxx-xxx-xxxx'
					type='tel'
				/>
			</Item>

			<Item label='Address' name='address'>
				<Input />
			</Item>
			<Item label='City' name='city'>
				<Input />
			</Item>
			<Item label='State' name='state'>
				<Input />
			</Item>
			<Item
				label='Country'
				name='country'
				rules={[
					{
						required: true,
						message: 'Country is required',
					},
				]}
			>
				<Input />
			</Item>
			<Item label='Website' name='website'>
				<Input />
			</Item>

			<Divider />

			<Title level={4}>Customer Success</Title>

			<Item label='Customer Success Managers' name='csmUser'>
				<Select
					placeholder='Select Customer Success Managers'
					options={csmUsersOptions}
					optionFilterProp='label'
					mode='multiple'
					allowClear
					showSearch
					loading={csmUsersOptions.length === 0}
				/>
			</Item>

			<Item
				label='Notification Emails'
				name='notificationEmails'
				rules={[
					() => ({
						validator(_, emails: string[]) {
							for (let index = 0; index < emails.length; index += 1) {
								const email = emails[index]
								if (isValidEmail(email) === false) {
									return Promise.reject(new Error(`Invalid Email Provided: ${email}`))
								}
							}
							return Promise.resolve()
						},
					}),
				]}
			>
				<Select placeholder='Add all emails to notify' mode='tags' />
			</Item>

			<Divider />

			<Title level={4}>Billing</Title>

			<Item
				label='Currency'
				name='currency_selected'
				rules={[{ required: true, message: 'Currency is required' }]}
			>
				<Select
					placeholder='Select Currency'
					options={supportedCurrencies.map(c => ({
						label: c,
						value: c,
						disabled: !['INR', 'USD'].includes(c),
					}))}
				/>
			</Item>
			<Item
				label='WhatCX Charge'
				name='whatcxCharge'
				rules={[
					{ required: true, message: 'WhatCX Charge is required' },
					{
						validator: (_, value) => {
							const parsed = normalizeClientChargeForSave(value)
							if (parsed < -1) {
								return Promise.reject(
									new Error('Must be -1 (default) or a positive number')
								)
							}
							return Promise.resolve()
						},
					},
				]}
				tooltip='Use -1 to fall back to the pricing chart default'
			>
				<Input
					placeholder='Enter WhatCX Charge (-1 for default)'
					type='number'
					min={-1}
					step='0.0001'
				/>
			</Item>

			<Item
				label='Inbound Conversation Charge'
				name='whatcxChargeInbound'
				rules={[
					{
						type: 'number',
						min: -1,
						message: 'Must be -1 (default) or a positive number',
					},
					{ required: false },
				]}
			>
				<InputNumber
					placeholder='Leave empty to use pricing chart default (-1)'
					min={-1}
					step={0.0001}
					precision={4}
					style={{ width: '100%' }}
				/>
			</Item>

			<Item
				label='Outbound Conversation Charge'
				name='whatcxChargeOutbound'
				rules={[
					{
						type: 'number',
						min: -1,
						message: 'Must be -1 (default) or a positive number',
					},
					{ required: false },
				]}
			>
				<InputNumber
					placeholder='Leave empty to use pricing chart default (-1)'
					min={-1}
					step={0.0001}
					precision={4}
					style={{ width: '100%' }}
				/>
			</Item>

			<Item
				label='Billing Emails'
				name='invoiceEmails'
				rules={[
					() => ({
						validator(_, emails: string[]) {
							for (let index = 0; index < emails.length; index += 1) {
								const email = emails[index]
								if (isValidEmail(email) === false) {
									return Promise.reject(new Error(`Invalid Email Provided: ${email}`))
								}
							}

							return Promise.resolve()
						},
					}),
				]}
			>
				<Select placeholder='Add all emails to notify' mode='tags' />
			</Item>

			<Item name='tdsEnabled' valuePropName='checked'>
				<Checkbox>Will deduct TDS</Checkbox>
			</Item>

			<Item name='shouldGenerateAutoInvoice' valuePropName='checked'>
				<Checkbox>Should Auto Generate Wallet Invoice</Checkbox>
			</Item>

			<Divider />

			<List name='invoiceDetails'>
				{(fields, { add, remove }) => (
					<>
						<Title level={4}>Invoice Details</Title>
						{fields.map(field => (
							<div key={field.key}>
								<Space>
									<Item
										label='Label'
										name={[field.name, 'key']}
										rules={[{ required: true, message: 'Invoice label is required' }]}
									>
										<Input />
									</Item>
									<Item
										label='Value'
										name={[field.name, 'value']}
										rules={[{ required: true, message: 'Invoice value is required' }]}
									>
										<Input />
									</Item>
									<Button
										htmlType='button'
										type='text'
										danger
										icon={<MinusCircleOutlined />}
										onClick={() => remove(field.name)}
									/>
								</Space>
							</div>
						))}
						<Button
							htmlType='button'
							icon={<PlusOutlined />}
							onClick={() =>
								add({
									key: '',
									value: '',
								})
							}
						>
							Add Details
						</Button>
					</>
				)}
			</List>

			<Divider />

			<List name='taxation'>
				{(fields, { add, remove }) => (
					<>
						<Title level={4}>Taxation</Title>
						{fields.map(field => (
							<div key={field.key}>
								<Space>
									<Item
										label='Label'
										name={[field.name, 'key']}
										rules={[{ required: true, message: 'Tax label is required' }]}
									>
										<Input />
									</Item>
									<Item
										label='Precentage'
										name={[field.name, 'percentage']}
										rules={[{ required: true, message: 'Tax percentage is required' }]}
									>
										<Input type='number' min={0} max={100} step='0.0001' />
									</Item>
									<Button
										htmlType='button'
										type='text'
										danger
										icon={<MinusCircleOutlined />}
										onClick={() => remove(field.name)}
									/>
								</Space>
							</div>
						))}
						<Button
							htmlType='button'
							icon={<PlusOutlined />}
							onClick={() =>
								add({
									key: '',
									percentage: '',
								})
							}
						>
							Add Tax
						</Button>
					</>
				)}
			</List>

			<Divider />
			<Row justify='space-between'>
				<Col>
					<Title level={4}>Plan Details</Title>
				</Col>

				<Col>
					<Dropdown
						overlay={
							<Menu onClick={handlePlanSelect}>
								{plans.map(ele => (
									<Menu.Item key={ele.id}>{ele.title}</Menu.Item>
								))}
							</Menu>
						}
					>
						<Button>
							<Space>Select Plan</Space>
						</Button>
					</Dropdown>
				</Col>
			</Row>
			<Item
				label='Title'
				name='title'
				rules={[{ required: true, message: 'Title is required' }]}
			>
				<Input />
			</Item>

			<Item
				label='Platform'
				name='platform'
				rules={[{ required: true, message: 'Platform is Required!' }]}
			>
				<Select
					showSearch
					optionFilterProp='label'
					options={edgePlatforms.map(platform => ({
						label: getPlatformLabel(platform),
						value: platform,
					}))}
				/>
			</Item>

			<Item
				label='Storage Allowed'
				name='storageAllowed'
				rules={[
					{
						required: true,
						message: 'Storage allowed data is required',
					},
				]}
			>
				<Input type='number' suffix='GB' min={0} />
			</Item>
			<Item
				label='Broadcast Limit'
				rules={[
					{
						required: true,
						message: 'Broadcast Limit is required',
					},
				]}
				name='broadcastLimit'
			>
				<Input type='number' min={0} />
			</Item>
			<Item
				label='Billing Cycle'
				name='billingCycle'
				rules={[
					{
						required: true,
						message: 'Billing cycle data is required',
					},
				]}
			>
				<Radio.Group
					onChange={e => setClientBillingCycle(e.target.value)}
					value={clientBillingCycle}
					options={allBillingCycle.map(b => ({
						value: b,
						label: toTileCase(b),
					}))}
				/>
			</Item>
			{clientBillingCycle !== null && (
				<Item
					label={
						clientBillingCycle === 'MONTHLY'
							? 'Per Month'
							: // TODO: Enable before invoice revamp
							// : clientBillingCycle === 'QUARTERLY'
							// ? 'Per Quarter'
							// : clientBillingCycle === 'HALF_YEARLY'
							// ? 'Per Half Year'
							clientBillingCycle === 'YEARLY'
							? 'Per Year'
							: 'Unkown'
					}
					name='billingCycleAmount'
					rules={[
						{
							required: true,
							message: 'Billing cycle amount data is required',
						},
					]}
				>
					<Input type='number' min={0} />
				</Item>
			)}

			<h4>Modules</h4>
			<AllowedModulesTable allowedModules={allowedModules} />

			<Item>
				<Button type='primary' htmlType='submit' block loading={loading}>
					Create Client
				</Button>
			</Item>
		</Form>
	)
}

export default AddClientForm
